import React from 'react';
import LegalPageLayout from './homepage/LegalPageLayout';

const EFFECTIVE_DATE = "May 11, 2026";

function Privacy() {
  const contents = [
    { id: "s1", title: "Introduction" },
    { id: "s2", title: "Information We Collect" },
    { id: "s3", title: "How We Use Your Information" },
    { id: "s4", title: "Data Storage & Security" },
    { id: "s5", title: "Third-Party Services" },
    { id: "s6", title: "Data Retention" },
    { id: "s7", title: "Your Rights" },
    { id: "s8", title: "Cookies & Sessions" },
    { id: "s9", title: "Changes to This Policy" },
    { id: "s10", title: "Contact Information" },
  ];

  return (
    <LegalPageLayout
      title="Privacy Policy"
      effectiveDate={EFFECTIVE_DATE}
      description={
        <>
          This Privacy Policy describes how <strong>XcelerateAI Inc.</strong> ("we", "us", or "our") collects,
          uses, and safeguards information when you use <strong>BookBridge</strong> (the "Service") —
          a developer middleware that connects applications to BookBridge via OAuth 2.0.
        </>
      }
    >
      {/* Table of Contents */}
      <nav className="bg-gradient-to-br from-soft to-white border border-line rounded-2xl p-8 mb-12 shadow-sm" aria-label="Table of contents">
        <h4 className="text-lg font-bold text-navy mb-6">Contents</h4>
        <ol className="space-y-3">
          {contents.map((item, index) => (
            <li key={item.id}>
              <a 
                href={`#${item.id}`} 
                className="text-ink hover:text-indigo-600 transition-colors font-medium"
              >
                {index + 1}. {item.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {/* Content Sections */}
      <section id="s1" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">1. Introduction</h2>
        <p className="text-lg text-muted leading-relaxed mb-4">
          BookBridge is a developer-focused, business-to-business tool that enables applications to
          authenticate with BookBridge and perform accounting operations on behalf of authorized users.
          This policy applies to anyone who registers an account on a deployment of the Service or whose
          credentials and OAuth tokens are stored within it.
        </p>
        <p className="text-lg text-muted leading-relaxed">
          Because the Service is a backend component installed and operated by developers and businesses, the
          ultimate data controller may be the operator of a given deployment rather than XcelerateAI Inc.
          Where this policy refers to "we", it describes the default behavior of the software as published.
        </p>
      </section>

      <section id="s2" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">2. Information We Collect</h2>
        <p className="text-lg text-muted leading-relaxed mb-6">The Service collects only the information required to authenticate users and communicate with BookBridge:</p>

        <h3 className="text-xl font-semibold text-navy mt-6 mb-3">Account information</h3>
        <ul className="space-y-3 text-lg text-muted mb-6">
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Email address</strong> — used as your unique identifier for sign-in.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Password</strong> — never stored in plaintext. Passwords are hashed with <code className="font-mono text-sm bg-soft border border-line px-2 py-1 rounded">bcrypt</code> before being saved.</span>
          </li>
        </ul>

        <h3 className="text-xl font-semibold text-navy mt-6 mb-3">BookBridge OAuth tokens</h3>
        <ul className="space-y-3 text-lg text-muted mb-6">
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Access token</strong> and <strong>refresh token</strong> issued by BookBridge, encrypted at rest.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>Token expiration timestamps required to refresh credentials automatically.</span>
          </li>
        </ul>

        <h3 className="text-xl font-semibold text-navy mt-6 mb-3">BookBridge company / organization data</h3>
        <ul className="space-y-3 text-lg text-muted mb-6">
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Company name</strong> and <strong>realm ID</strong> (the BookBridge organization identifier) returned by BookBridge when you connect.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>Additional company metadata fetched from BookBridge only as needed to fulfill API requests you initiate.</span>
          </li>
        </ul>

        <h3 className="text-xl font-semibold text-navy mt-6 mb-3">Session data</h3>
        <ul className="space-y-3 text-lg text-muted mb-6">
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>An HTTP-only session cookie named <code className="font-mono text-sm bg-soft border border-line px-2 py-1 rounded">bb.sid</code>.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>Short-lived <strong>JWT tokens</strong> used to authorize API requests.</span>
          </li>
        </ul>

        <h3 className="text-xl font-semibold text-navy mt-6 mb-3">Server logs</h3>
        <ul className="space-y-3 text-lg text-muted">
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>API request logs including endpoint paths, HTTP methods, timestamps, status codes, and the originating IP address.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>Logs are intended for security monitoring, debugging, and abuse prevention.</span>
          </li>
        </ul>
      </section>

      <section id="s3" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">3. How We Use Your Information</h2>
        <ul className="space-y-3 text-lg text-muted mb-4">
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>To <strong>authenticate users</strong> and maintain authorized sessions.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>To <strong>connect to and communicate with BookBridge</strong> on your behalf via the API.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>To <strong>automatically refresh OAuth tokens</strong> before they expire so your integration stays connected.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>To <strong>provide access to BookBridge accounting data</strong> through the Service's REST API endpoints.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>To monitor the security and reliability of the Service and to investigate misuse.</span>
          </li>
        </ul>
        <p className="text-lg text-muted leading-relaxed">
          We do not sell personal information, and we do not use your data for advertising or profiling.
        </p>
      </section>

      <section id="s4" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">4. Data Storage & Security</h2>
        <p className="text-lg text-muted leading-relaxed mb-6">The Service applies industry-standard practices to protect the information it stores:</p>
        <ul className="space-y-3 text-lg text-muted mb-6">
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>MongoDB</strong> is used as the persistent data store for accounts, tokens, and organization records.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>bcrypt</strong> is used to hash all user passwords; plaintext passwords are never stored or logged.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>OAuth tokens are encrypted at rest</strong> using a server-side secret before being written to the database.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>HTTP-only cookies</strong> are used for session storage so client-side JavaScript cannot read them.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>JWT tokens</strong> are signed with a server-side secret and validated on every authenticated request.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Environment variables</strong> protect secrets such as the BookBridge client ID and secret, JWT signing key, and token-encryption key.</span>
          </li>
        </ul>
        <p className="text-lg text-muted leading-relaxed">
          No system can be guaranteed to be 100% secure. Operators deploying the Service are responsible for
          enforcing transport security (HTTPS), keeping dependencies up to date, and protecting their environment
          variables and database backups.
        </p>
      </section>

      <section id="s5" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">5. Third-Party Services</h2>
        <p className="text-lg text-muted leading-relaxed mb-6">The Service relies on the following third party:</p>
        <ul className="space-y-3 text-lg text-muted mb-6">
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span>
              <strong>BookBridge</strong> — the OAuth provider and the source of all accounting data
              accessed through the Service. Your use of BookBridge data is governed by{" "}
              <a href="https://www.intuit.com/privacy/" target="_blank" rel="noreferrer" className="text-indigo-600 underline underline-offset-2 hover:no-underline">BookBridge's own privacy policy</a>{" "}
              and the BookBridge terms of service.
            </span>
          </li>
        </ul>
        <p className="text-lg text-muted leading-relaxed">
          The Service does not transmit personal data to any other third party in its default configuration.
        </p>
      </section>

      <section id="s6" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">6. Data Retention</h2>
        <ul className="space-y-3 text-lg text-muted">
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Access tokens</strong> issued by BookBridge expire approximately <strong>1 hour</strong> after issuance.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Refresh tokens</strong> issued by BookBridge expire approximately <strong>100 days</strong> after their last use.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Account data</strong> (email, hashed password, connected organizations) is retained until you delete your account or the operator deletes it on your behalf.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Server logs</strong> are retained for the period configured by the operator (defaults vary by deployment).</span>
          </li>
        </ul>
      </section>

      <section id="s7" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">7. Your Rights</h2>
        <p className="text-lg text-muted leading-relaxed mb-6">You may, at any time:</p>
        <ul className="space-y-3 text-lg text-muted mb-6">
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Disconnect BookBridge</strong> from your account, which removes the stored OAuth tokens and revokes the Service's access to your BookBridge organization.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Delete your account</strong>, which removes your email, password hash, organization records, and stored tokens from the database.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Request a copy</strong> of the personal data the Service holds about you by contacting the operator of your deployment.</span>
          </li>
        </ul>
        <p className="text-lg text-muted leading-relaxed">
          Depending on your jurisdiction, you may have additional rights under laws such as the GDPR or CCPA.
          Contact the operator of your deployment to exercise those rights.
        </p>
      </section>

      <section id="s8" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">8. Cookies & Sessions</h2>
        <p className="text-lg text-muted leading-relaxed mb-6">The Service uses a single first-party session cookie:</p>
        <ul className="space-y-3 text-lg text-muted mb-6">
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Name:</strong> <code className="font-mono text-sm bg-soft border border-line px-2 py-1 rounded">bb.sid</code></span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Purpose:</strong> Maintains your authenticated session between API requests.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Max age:</strong> 7 days</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-indigo-600 mt-1">•</span>
            <span><strong>Flags:</strong> <code className="font-mono text-sm bg-soft border border-line px-2 py-1 rounded">HttpOnly</code> (and <code className="font-mono text-sm bg-soft border border-line px-2 py-1 rounded">Secure</code> when served over HTTPS)</span>
          </li>
        </ul>
        <p className="text-lg text-muted leading-relaxed">
          The Service does not set advertising or analytics cookies.
        </p>
      </section>

      <section id="s9" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">9. Changes to This Policy</h2>
        <p className="text-lg text-muted leading-relaxed">
          We may update this Privacy Policy from time to time to reflect changes in the Service, applicable laws,
          or operational practices. When we do, we will revise the "Last updated" date at the top of this page.
          Significant changes will be communicated to operators of the Service through release notes.
        </p>
      </section>

      <section id="s10" className="mb-12">
        <h2 className="text-2xl font-bold text-navy mb-4 pb-4 border-b border-line">10. Contact Information</h2>
        <p className="text-lg text-muted leading-relaxed mb-6">For questions or concerns about this Privacy Policy or our data practices, contact us at:</p>
        <div className="bg-gradient-to-br from-soft to-white p-8 rounded-2xl border border-line shadow-sm">
          <p className="text-lg mb-2"><strong>XcelerateAI Inc.</strong></p>
          <p className="text-lg text-muted">
            Email: <a href="mailto:support@xcelerateai.com" className="text-indigo-600 underline underline-offset-2 hover:no-underline">support@xcelerateai.com</a>
          </p>
        </div>
      </section>
    </LegalPageLayout>
  );
}

export default Privacy;
